import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Compact, accessible <FilterSelect> — a custom dropdown aligned with the
 * editorial aesthetic of the site. Replaces horizontal pill rows that used to
 * take half the viewport. Options: [{value, label, count?}].
 *
 *   <FilterSelect label="Ville" value={city} onChange={setCity}
 *       options={[{value:"",label:"Toutes"},{value:"eu",label:"Eu"}]} />
 */
export default function FilterSelect({
    label,
    value,
    onChange,
    options,
    testId = "filter",
    className = "",
    placeholder = "Toutes",
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onClick(e) {
            if (!ref.current || ref.current.contains(e.target)) return;
            setOpen(false);
        }
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const current = options.find((o) => o.value === value) || options[0];

    return (
        <div ref={ref} className={`relative ${className}`} data-testid={testId}>
            <span className="cp-kicker text-[9px] text-cp-gris mb-1.5 block">{label}</span>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 border ${
                    open ? "border-cp-foret" : "border-cp-border"
                } bg-cp-ivoire hover:border-cp-foret text-left transition-colors`}
                data-testid={`${testId}-button`}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="cp-serif text-sm md:text-base text-cp-encre truncate">
                    {current?.label || placeholder}
                </span>
                <ChevronDown
                    size={14}
                    strokeWidth={1.5}
                    className={`text-cp-foret transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <ul
                    className="absolute left-0 right-0 mt-1.5 z-40 bg-cp-ivoire border border-cp-foret/40 shadow-xl max-h-[380px] overflow-auto py-1"
                    role="listbox"
                    data-testid={`${testId}-list`}
                >
                    {options.map((o) => (
                        <li key={o.value || "__all__"}>
                            <button
                                type="button"
                                onClick={() => {
                                    onChange(o.value);
                                    setOpen(false);
                                }}
                                data-testid={`${testId}-option-${o.value || "all"}`}
                                className={`w-full flex items-center justify-between px-4 py-2 text-left cp-serif text-sm hover:bg-cp-sable/20 transition-colors ${
                                    o.value === value ? "bg-cp-foret/8 text-cp-foret italic" : "text-cp-noir"
                                }`}
                            >
                                <span>{o.label}</span>
                                {typeof o.count === "number" && (
                                    <span className="cp-tiny text-cp-gris">{o.count}</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
